import { useCallback, useEffect, useState } from 'react';

import { useRequest } from 'api';
import { useTranslationX } from 'i18n';
import { classes, gtag } from 'utils';
import { Button, Loader } from 'components/ui';
import { Action, State } from './SignIn';

import styles from './CodeConfirm.module.scss';

const CODE_SIZE = 5;

type Props = {
  state: State;
  dispatch: React.Dispatch<Action>;
};

const CodeConfirm = (props: Props) => {
  const { state, dispatch } = props;

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const [resend, setResend] = useState(() => {
    const remaining = state.response?.remaining || 0;

    return remaining > 0
      ? {
          time: remaining + 1000,
          status: 1,
        }
      : { time: 0, status: 0 };
  });

  const { tx } = useTranslationX('play.auth.code');
  const request = useRequest({ autoclear: true });

  useEffect(() => {
    if (resend?.status && resend.time) {
      const timer = setTimeout(() => setResend({ time: 0, status: 0 }), resend.time);
      return () => clearTimeout(timer);
    }
  }, [resend]);

  const submit = useCallback(
    (data: string, dataType: string, code: string) => {
      setBusy(true);
      gtag('event', 'confirm_code_cliked');

      request<string>({
        url: '/userInteraction/confirmValidation',
        method: 'POST',
        data: { data, dataType, code, origin: 'plays' },
        onSuccess: token => dispatch({ type: 'DONE', payload: token }),
        onError: ({ response }) =>
          dispatch({
            type: 'ERROR',
            payload:
              response?.status === 400
                ? 'play.auth.code.error.invalid'
                : 'play.auth.error.unknown',
          }),
        onComplete: () => setBusy(false),
      });
    },
    [dispatch, request]
  );

  const requestCode = useCallback(
    (data: string, dataType: string) => {
      gtag('event', 'request_code_cliked');

      request<{ remaining: number }>({
        url: '/userInteraction/requestValidation',
        method: 'POST',
        data: { data, dataType },
        onSuccess: ({ remaining }) => setResend({ time: remaining + 1000, status: 1 }),
      });
    },
    [request]
  );

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '');

    if (value.length <= 5) {
      setCode(value);
    }
  };

  const renderContent = () => {
    if (busy) {
      return (
        <div className={styles.loader}>
          <Loader />
        </div>
      );
    }

    return (
      <>
        <input
          className={styles.input}
          value={code}
          onChange={onChange}
          placeholder={tx('placeholder')}
        />

        <Button
          style={{
            root: {
              height: '2.2rem',
              width: '100%',
              visibility: code.length === CODE_SIZE ? 'visible' : 'hidden',
            },
          }}
          onClick={() =>
            state.response && code.length === CODE_SIZE
              ? submit(state.response.data, state.response.dataType, code)
              : null
          }
        >
          {tx('button')}
        </Button>

        <div
          className={classes(styles.resend, styles[`s${resend.status}`])}
          style={{ '--animation-time': `${resend.time}ms` } as any}
          onClick={() =>
            state.response && resend.status === 0
              ? requestCode(state.response.data, state.response.dataType)
              : null
          }
        >
          <span className={styles.text}>{tx('resend')}</span>

          <span className={styles.bar}></span>
        </div>
      </>
    );
  };

  return <div className={styles.code}>{renderContent()}</div>;
};

export default CodeConfirm;
